import { Vector2 } from './vector';
import { createBody, resetIdCounter } from './body';
import { PhysicsWorld } from './world';

export interface Scenario {
  name: string;
  description: string;
  setup: () => PhysicsWorld;
}

export const SCENARIOS: Scenario[] = [
  {
    name: 'Solar System',
    description: 'A central star with orbiting planets at varied distances.',
    setup: () => {
      resetIdCounter();
      const world = new PhysicsWorld({ G: 0.8, softening: 2 });
      world.addBody(createBody(new Vector2(0, 0), new Vector2(0, 0), 800, '#FDB813', true));
      const colors = ['#A0522D', '#C0C0C0', '#4169E1', '#FF4500', '#DAA520', '#CD853F', '#87CEEB', '#0000CD'];
      const distances = [60, 90, 130, 170, 240, 320, 420, 540];
      const masses = [3, 5, 6, 4, 15, 12, 10, 9];
      for (let i = 0; i < distances.length; i++) {
        const d = distances[i];
        const v = Math.sqrt((world.config.G * 800) / d);
        world.addBody(createBody(new Vector2(d, 0), new Vector2(0, v), masses[i], colors[i]));
      }
      return world;
    },
  },
  {
    name: 'Binary Stars',
    description: 'Two massive stars in a tight mutual orbit.',
    setup: () => {
      resetIdCounter();
      const world = new PhysicsWorld({ G: 1.2, softening: 3 });
      const mass = 500;
      const dist = 80;
      const v = Math.sqrt((world.config.G * mass) / (2 * dist));
      world.addBody(createBody(new Vector2(-dist, 0), new Vector2(0, v), mass, '#FF6B6B'));
      world.addBody(createBody(new Vector2(dist, 0), new Vector2(0, -v), mass, '#4ECDC4'));
      return world;
    },
  },
  {
    name: 'Figure Eight',
    description: 'The famous stable three-body figure-eight orbit.',
    setup: () => {
      resetIdCounter();
      const world = new PhysicsWorld({ G: 1, softening: 0.5, dt: 0.4 });
      const m = 100;
      world.addBody(createBody(new Vector2(0.97000436, -0.24308753), new Vector2(0.466203685, 0.43236573), m, '#FF6B6B'));
      world.addBody(createBody(new Vector2(-0.97000436, 0.24308753), new Vector2(0.466203685, 0.43236573), m, '#4ECDC4'));
      world.addBody(createBody(new Vector2(0, 0), new Vector2(-0.93240737, -0.86473146), m, '#FFE66D'));
      // Scale up for visibility
      for (const b of world.bodies) {
        b.pos = b.pos.mul(80);
        b.vel = b.vel.mul(0.8);
      }
      return world;
    },
  },
  {
    name: 'Chaos Cluster',
    description: 'Twenty random bodies in a dense, unstable cluster.',
    setup: () => {
      resetIdCounter();
      const world = new PhysicsWorld({ G: 0.6, softening: 5, dt: 0.4 });
      const colors = ['#FF6B6B', '#4ECDC4', '#FFE66D', '#95E1D3', '#F38181', '#AA96DA', '#FCBAD3', '#FFFFD2'];
      for (let i = 0; i < 20; i++) {
        const angle = Math.random() * Math.PI * 2;
        const dist = 30 + Math.random() * 200;
        const pos = new Vector2(Math.cos(angle) * dist, Math.sin(angle) * dist);
        const vel = new Vector2((Math.random() - 0.5) * 2, (Math.random() - 0.5) * 2);
        const mass = 10 + Math.random() * 40;
        world.addBody(createBody(pos, vel, mass, colors[i % colors.length]));
      }
      return world;
    },
  },
  {
    name: 'Lagrange Points',
    description: 'A planet at L4 and L5 points of a star-planet system.',
    setup: () => {
      resetIdCounter();
      const world = new PhysicsWorld({ G: 1, softening: 2 });
      const starMass = 1000;
      const planetMass = 50;
      const dist = 150;
      const v = Math.sqrt(world.config.G * starMass / dist);

      world.addBody(createBody(new Vector2(0, 0), new Vector2(0, 0), starMass, '#FDB813', true));
      world.addBody(createBody(new Vector2(dist, 0), new Vector2(0, v), planetMass, '#4169E1'));

      // L4 and L5 (60 degrees ahead and behind)
      const l4 = new Vector2(dist * Math.cos(Math.PI / 3), dist * Math.sin(Math.PI / 3));
      const l5 = new Vector2(dist * Math.cos(-Math.PI / 3), dist * Math.sin(-Math.PI / 3));
      const vl4 = new Vector2(-v * Math.sin(Math.PI / 3), v * Math.cos(Math.PI / 3));
      const vl5 = new Vector2(-v * Math.sin(-Math.PI / 3), v * Math.cos(-Math.PI / 3));

      world.addBody(createBody(l4, vl4, 8, '#FF6B6B'));
      world.addBody(createBody(l5, vl5, 8, '#4ECDC4'));
      return world;
    },
  },
];
